import React from "react";
import {
  DollarSign,
  TrendingUp,
  Building2,
  BarChart3,
  Scale,
} from "lucide-react";

interface QuickActionsMenuProps {
  showQuickActionsMenu: boolean;
  onTriggerAction: (actionName: string) => void;
  onClose: () => void;
  actionsConfig?: {
    enableFinancialSummary: boolean;
    enableOpportunityCost: boolean;
    enableWingAnalysis?: boolean;
    enableTenantRanking?: boolean;
    enableTenantComparison?: boolean;
  };
  hasSelectedSpaces?: boolean;
  /** Number of currently selected spaces (tenant insights require exactly one). */
  selectedSpacesCount?: number;
}

const QuickActionsMenu: React.FC<QuickActionsMenuProps> = ({
  showQuickActionsMenu,
  onTriggerAction,
  onClose,
  actionsConfig = {
    enableFinancialSummary: true,
    enableOpportunityCost: true,
    enableWingAnalysis: true,
    enableTenantRanking: true,
    enableTenantComparison: true,
  },
  hasSelectedSpaces = true,
  selectedSpacesCount = 0,
}) => {
  if (!showQuickActionsMenu) {
    return null;
  }

  // These default ON; only an explicit `false` hides them so a stale exported
  // runtime config can't suppress them.
  const wingEnabled = actionsConfig.enableWingAnalysis !== false;
  const tenantRankingEnabled = actionsConfig.enableTenantRanking !== false;
  const tenantComparisonEnabled = actionsConfig.enableTenantComparison !== false;
  const anyTenantInsight = tenantRankingEnabled || tenantComparisonEnabled;
  const isSingleSelection = selectedSpacesCount === 1;

  // Check if at least one action is enabled
  const hasEnabledActions =
    actionsConfig.enableFinancialSummary ||
    actionsConfig.enableOpportunityCost ||
    wingEnabled ||
    anyTenantInsight;

  if (!hasEnabledActions) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose}></div>
      <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-slate-100 py-1 z-40 animate-in fade-in zoom-in-95 duration-100">
        {!hasSelectedSpaces ? (
          <div className="px-2 py-1">
            <div className="py-2 flex items-center justify-center text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md border border-indigo-100 text-center px-2">
              Use tools to select spaces
            </div>
          </div>
        ) : (
          <>
            <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
              Quick Analysis
            </div>

            {actionsConfig.enableFinancialSummary && (
              <button
                onClick={() => onTriggerAction("Financial Summary")}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 transition"
              >
                <DollarSign size={14} className="text-green-500" />
                <span>Financial Summary</span>
              </button>
            )}

            {actionsConfig.enableOpportunityCost && (
              <button
                onClick={() => onTriggerAction("Opportunity Cost")}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 transition"
              >
                <TrendingUp size={14} className="text-purple-500" />
                <span>Opportunity Cost</span>
              </button>
            )}

            {wingEnabled && (
              <button
                onClick={() => onTriggerAction("Wing Analysis")}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 transition"
              >
                <Building2 size={14} className="text-blue-500" />
                <span>Wing Analysis</span>
              </button>
            )}

            {/* Space-level tenant insights — require a single selected space */}
            {anyTenantInsight && (
              <>
                <div className="mt-1 px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-t border-slate-100">
                  Space Insights
                </div>

                {!isSingleSelection ? (
                  <div className="px-2 pb-1">
                    <div className="py-1.5 flex items-center justify-center text-[11px] text-amber-700 bg-amber-50 rounded-md border border-amber-100 text-center px-2">
                      Select a single space for tenant insights
                    </div>
                  </div>
                ) : (
                  <>
                    {tenantRankingEnabled && (
                      <button
                        onClick={() => onTriggerAction("Tenant Ranking")}
                        className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 transition"
                      >
                        <BarChart3 size={14} className="text-emerald-500" />
                        <span>Tenant Ranking</span>
                      </button>
                    )}

                    {tenantComparisonEnabled && (
                      <button
                        onClick={() => onTriggerAction("Tenant Comparison")}
                        className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 transition"
                      >
                        <Scale size={14} className="text-orange-500" />
                        <span>Tenant Comparison</span>
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default QuickActionsMenu;
